import { useEffect, useCallback } from 'react'

export interface KeyboardShortcut {
  key: string
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  description: string
  action: () => void
  preventDefault?: boolean
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean
  shortcuts: KeyboardShortcut[]
}

export const useKeyboardShortcuts = ({ enabled = true, shortcuts }: UseKeyboardShortcutsOptions) => {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return

    // 入力フィールドやテキストエリアでは無効化
    const target = event.target as HTMLElement
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.isContentEditable
    ) {
      // ただし、Escapeキーは常に有効
      if (event.key !== 'Escape') {
        return
      }
    }

    for (const shortcut of shortcuts) {
      const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
      const ctrlMatches = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey
      const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey
      const altMatches = shortcut.alt ? event.altKey : !event.altKey

      if (keyMatches && ctrlMatches && shiftMatches && altMatches) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault()
        }
        shortcut.action()
        break
      }
    }
  }, [enabled, shortcuts])

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [enabled, handleKeyDown])
}

// グローバルショートカット定義
export const GLOBAL_SHORTCUTS = {
  NEW_TASK: { key: 'n', description: '新しいタスクを作成' },
  SEARCH: { key: '/', description: '検索' },
  HELP: { key: '?', shift: true, description: 'ショートカット一覧を表示' },
  TOGGLE_SIDEBAR: { key: 'b', ctrl: true, description: 'サイドバーの表示/非表示' },
  FOCUS_PROJECTS: { key: 'p', ctrl: true, description: 'プロジェクト一覧にフォーカス' },
  FOCUS_TASKS: { key: 't', ctrl: true, description: 'タスク一覧にフォーカス' },
  TOGGLE_THEME: { key: 'd', ctrl: true, description: 'ダークモード切り替え' },
  SAVE: { key: 's', ctrl: true, description: '保存' },
  CLOSE: { key: 'Escape', description: 'モーダルを閉じる' },
}

// ショートカットのフォーマット表示用
export const formatShortcut = (shortcut: Partial<KeyboardShortcut>): string => {
  const parts: string[] = []

  if (shortcut.ctrl || shortcut.meta) {
    // Mac では Cmd、その他では Ctrl
    parts.push(navigator.platform.includes('Mac') ? '⌘' : 'Ctrl')
  }
  if (shortcut.shift) parts.push('Shift')
  if (shortcut.alt) parts.push('Alt')

  if (shortcut.key) {
    // 特殊キーの表示名を変換
    const keyDisplay = {
      'Escape': 'Esc',
      'ArrowUp': '↑',
      'ArrowDown': '↓',
      'ArrowLeft': '←',
      'ArrowRight': '→',
      ' ': 'Space',
    }[shortcut.key] || shortcut.key.toUpperCase()

    parts.push(keyDisplay)
  }

  return parts.join(' + ')
}
